import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Ticket, TicketStatus } from "@/lib/types";
import { Figure, Panel } from "@/ui/Panel";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { Filter } from "@/ui/Filter";
import { Chip } from "@/ui/Chip";
import { Lamp } from "@/ui/Lamp";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, relativeTime } from "@/lib/format";
import {
  PRIORITY_LABEL,
  STAFF_STATUS_LABEL,
  STATUS_TONE,
  serviceSummary,
} from "@/features/support/tickets";

type Show = "needs_us" | "unassigned" | "waiting" | "all";

export const Route = createFileRoute("/_app/admin/tickets/")({
  component: TicketQueue,
  // In the address, like every other filtered staff screen, so the view
  // somebody is looking at can be pasted into a message.
  validateSearch: (search: Record<string, unknown>): { show?: Show } => {
    const v = search.show;
    return v === "unassigned" || v === "waiting" || v === "all" ? { show: v } : {};
  },
});

interface QueueResponse {
  tickets: Ticket[] | null;
  counts: { needs_us: number; waiting_on_customer: number; unassigned: number };
}

/**
 * The support queue.
 *
 * # WHY THE DEFAULT VIEW IS "NEEDS A REPLY" AND NOT "EVERYTHING"
 *
 * Because a queue that shows everything is a list, and a list is something
 * people read rather than work. The three filters are the three questions a
 * person actually arrives with — what is mine to answer, what has nobody
 * picked up, and what am I waiting on somebody else for — and the fourth,
 * everything, is there for the times you are looking for one particular
 * ticket rather than working the queue.
 */
function TicketQueue() {
  const { show = "needs_us" } = Route.useSearch();
  const navigate = useNavigate();

  const params: Record<string, string | boolean> = {};
  // needs_us, not status=waiting_on_us.
  //
  // The second is what this sent before, and it hid every brand-new request:
  // a ticket is created as "open" and only becomes "waiting_on_us" once the
  // customer replies. So the default view of the queue showed tickets that
  // had already been answered once and left out the ones nobody had touched.
  // The count on the pill beside it had always included them, so the number
  // and the list disagreed.
  if (show === "needs_us") params.needs_us = true;
  if (show === "unassigned") {
    params.unassigned = true;
    params.open = true;
  }
  if (show === "waiting") params.status = "waiting_on_customer";

  const queue = useQuery({
    queryKey: ["admin-tickets", show],
    queryFn: ({ signal }) => api.get<QueueResponse>("/admin/tickets", params, signal),
    // A support queue that is stale is a support queue somebody reloads by
    // hand. Thirty seconds costs one small query and removes the habit.
    refetchInterval: 30_000,
  });

  const list = queue.data?.tickets ?? [];
  const counts = queue.data?.counts;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Figure
          label="Needs a reply"
          value={counts ? String(counts.needs_us) : "—"}
          hint="New, or the customer has answered"
          className="p-4"
        />
        <Figure
          label="Nobody has it"
          value={counts ? String(counts.unassigned) : "—"}
          hint="Open and unassigned"
          className="p-4"
        />
        <Figure
          label="Waiting on the customer"
          value={counts ? String(counts.waiting_on_customer) : "—"}
          hint="Answered, nothing back yet"
          className="p-4"
        />
      </div>

      <Panel
        padded={false}
        toolbar={
          <>
            <Filter
              label="Needs a reply"
              count={counts?.needs_us}
              active={show === "needs_us"}
              onClick={() => void navigate({ to: ".", search: {} })}
            />
            <Filter
              label="Unassigned"
              count={counts?.unassigned}
              active={show === "unassigned"}
              onClick={() => void navigate({ to: ".", search: { show: "unassigned" } })}
            />
            <Filter
              label="Waiting on customer"
              count={counts?.waiting_on_customer}
              active={show === "waiting"}
              onClick={() => void navigate({ to: ".", search: { show: "waiting" } })}
            />
            <Filter
              label="Everything"
              active={show === "all"}
              onClick={() => void navigate({ to: ".", search: { show: "all" } })}
            />
          </>
        }
      >
        {queue.isLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : queue.isError ? (
          <div className="p-[18px]">
            <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="Nothing here"
            description={
              show === "needs_us"
                ? "Every ticket has been answered. The customer has the ball on anything still open."
                : "No tickets match this filter."
            }
          />
        ) : (
          // A higher floor than the 34rem a fixed table gets by default.
          //
          // Five of the six columns are declared and they come to about 500
          // pixels between them, so at the default the Subject column — the
          // one a triager actually reads — was left with 46. Forty-four rem
          // leaves it around two hundred, which is a readable subject.
          <Table dense fixed minWidth="44rem">
            <thead>
              <Tr>
                <Th width="w-[16ch]">Ticket</Th>
                <Th>Subject</Th>
                {/* Sixteen, not twenty. The four characters go to Last
                    activity below, which needs them: this table's columns
                    are fixed, so widening one without narrowing another
                    makes the whole table 28 pixels too wide and it scrolls
                    sideways on a 1440px screen. An account name is clipped
                    at any width. */}
                <Th width="w-[16ch]">Account</Th>
                <Th width="w-[20ch]">State</Th>
                <Th width="w-[14ch]">Owner</Th>
                {/* Twenty-two characters of the 11px label font, which is
                    what "2 minutes ago" needs in the 13px data face plus the
                    cell's padding.
                    A declared column in a `table-fixed` table is a floor, not
                    a ceiling: a cell that may not wrap cannot shrink below
                    its content, so a column declared too narrow does not
                    truncate — it pushes the whole table wider and the table
                    scrolls sideways on a 1440px screen. Subject is the one
                    auto column and absorbs what these take. */}
                <Th width="w-[22ch]" align="right">
                  Last activity
                </Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <Tr key={t.id}>
                  <Td clip>
                    <Link
                      to="/admin/tickets/$ticketId"
                      params={{ ticketId: t.id }}
                      className="mono text-[12px] font-[600] text-[var(--primary)]"
                    >
                      {t.reference}
                    </Link>
                  </Td>
                  <Td
                    clip
                    title={t.subject}
                    // Which services, without breaking the one-line rule this
                    // table is built on. A triager should not have to open a
                    // technical ticket to find out what it is about.
                    secondary={serviceSummary(t.services)}
                  >
                    <Link
                      to="/admin/tickets/$ticketId"
                      params={{ ticketId: t.id }}
                      className="font-[550] hover:underline"
                    >
                      {t.subject}
                    </Link>
                    {t.priority !== "normal" && (
                      <Chip
                        tone={t.priority === "urgent" || t.priority === "high" ? "down" : undefined}
                        className="ml-2"
                      >
                        {PRIORITY_LABEL[t.priority]}
                      </Chip>
                    )}
                  </Td>
                  <Td clip title={t.organisation_name}>
                    {t.organisation_name}
                  </Td>
                  <Td clip>
                    <StaffStatus status={t.status} />
                  </Td>
                  <Td clip className="text-[var(--text-secondary)]">
                    {t.assignee_name || "—"}
                  </Td>
                  <Td numeric title={absoluteTime(t.last_message_at)}>
                    {relativeTime(t.last_message_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

/** The status in the operator's words, not the customer's. */
/**
 * A ticket's state, for the triager.
 *
 * The system's chip rather than a hand-rolled capsule. The capsule it
 * replaces was a `rounded-full` inline-flex with its own padding, and two
 * things were wrong with it: nothing in this system is a pill — the only
 * round things are lamps, and they mean something — and sitting on the text
 * baseline it pushed its row to 36.5px against 28 for every row without one.
 */
export function StaffStatus({ status }: { status: TicketStatus }) {
  const token = STATUS_TONE[status];
  return (
    <span className="chip gap-1.5" style={{ color: LAMP_FOR[token], borderColor: LAMP_FOR[token] }}>
      <Lamp live={status === "open" || status === "waiting_on_us"} />
      {STAFF_STATUS_LABEL[status]}
    </span>
  );
}

/** Exported so the detail screen can use the same token lookup. */
export { Chip };

/**
 * The lamp a state name lights.
 *
 * The screens used to compose a custom property name out of a variable —
 * `var(--color-state-${token}-fg)` — which works until the token set is
 * replaced and then fails silently, because a missing custom property is not
 * an error, it is simply nothing. This is a lookup, so an unknown state is a
 * compile-time concern rather than an invisible one.
 */
const LAMP_FOR: Record<string, string> = {
  running: "var(--success)",
  healthy: "var(--success)",
  degraded: "var(--warning)",
  suspended: "var(--warning)",
  failed: "var(--danger)",
  stopped: "var(--text-faint)",
  building: "var(--primary)",
};
